import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button, InputNumber, Divider, message, Spin } from 'antd';
import { ShoppingCartOutlined } from '@ant-design/icons';

import MainLayout from '../../components/Layout/MainLayout';
import Breadcrumbs from '../../components/shared/Breadcrumbs';
import PriceTag from '../../components/shared/PriceTag';
import ConditionBadge from '../../components/shared/ConditionBadge';
import StarRating from '../../components/shared/StarRating';
import BookCardSmall from '../../components/shared/BookCardSmall';
import { useApi } from '../../hooks/useApi';

import {
  PageWrapper,
  TwoColumnLayout,
  LeftColumn,
  RightColumn,
  CoverImageWrapper,
  CoverImage,
  ImagePlaceholder,
  BookTitle,
  AuthorLink,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  PriceSection,
  ActionRow,
  SynopsisSection,
  SynopsisTitle,
  SynopsisText,
  RecentSection,
  RecentTitle,
  RecentScroll,
  NotFoundWrapper,
  NotFoundTitle,
  NotFoundText,
  ListingsSection,
  ListingsTitle,
  ListingCard,
  ListingSellerInfo,
  ListingSellerName,
  ListingMeta,
  ListingCopies,
  ListingPriceArea,
  ListingThumbnail,
  ThumbnailLabel,
} from './styles';

export default function BookDetail() {
  const { id } = useParams();
  const { data: book, loading: bookLoading } = useApi(`/books/${id}`);
  const { data: recentBooks, loading: recentLoading } = useApi('/books/recent?count=6');

  const [selectedListingId, setSelectedListingId] = useState(null);
  const [quantity, setQuantity] = useState(1);

  if (bookLoading) {
    return (
      <MainLayout showSidebar={false}>
        <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
      </MainLayout>
    );
  }

  if (!book) {
    return (
      <MainLayout showSidebar={false}>
        <NotFoundWrapper>
          <NotFoundTitle>Libro no encontrado</NotFoundTitle>
          <NotFoundText>
            El libro que buscas no existe o fue removido.
          </NotFoundText>
          <Link to="/">
            <Button type="primary" style={{ marginTop: 16 }}>
              Volver al inicio
            </Button>
          </Link>
        </NotFoundWrapper>
      </MainLayout>
    );
  }

  const bookListings = book.listings || [];
  const effectiveSelectedId = selectedListingId
    ?? (bookListings.length > 0
      ? bookListings.reduce((min, l) => (l.price < min.price ? l : min)).id
      : null);

  const selectedListing = bookListings.find((l) => l.id === effectiveSelectedId) || null;

  const breadcrumbItems = [
    {
      label: book.categoryName || book.categoryId,
      to: `/buscar?categoria=${book.categoryId}`,
    },
    { label: book.title },
  ];

  const handleAddToCart = () => {
    message.success(`${quantity} copia(s) de "${book.title}" agregada(s) al carrito`);
  };

  const handleSelectListing = (listingId) => {
    setSelectedListingId(listingId);
    setQuantity(1);
  };

  return (
    <MainLayout showSidebar={false}>
      <PageWrapper>
        <Breadcrumbs items={breadcrumbItems} />

        <TwoColumnLayout>
          <LeftColumn>
            <CoverImageWrapper>
              {book.coverImage ? (
                <CoverImage src={book.coverImage} alt={book.title} />
              ) : (
                <ImagePlaceholder>Sin imagen</ImagePlaceholder>
              )}
            </CoverImageWrapper>
          </LeftColumn>

          <RightColumn>
            <BookTitle>{book.title}</BookTitle>
            <AuthorLink to={`/autor/${book.author?.id}`}>
              {book.author?.name}
            </AuthorLink>

            <Divider />

            <InfoGrid>
              <InfoItem>
                <InfoLabel>ISBN</InfoLabel>
                <InfoValue>{book.isbn}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Idioma</InfoLabel>
                <InfoValue>{book.language}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Editorial</InfoLabel>
                <InfoValue>{book.publisher}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Año</InfoLabel>
                <InfoValue>{book.year}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Tipo</InfoLabel>
                <InfoValue>{book.publicationType}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Encuadernación</InfoLabel>
                <InfoValue>{book.coverType}</InfoValue>
              </InfoItem>
            </InfoGrid>

            <Divider />

            {bookListings.length > 0 && (
              <ListingsSection>
                <ListingsTitle>
                  {bookListings.length === 1
                    ? '1 vendedor ofrece este libro'
                    : `${bookListings.length} vendedores ofrecen este libro`}
                </ListingsTitle>
                {bookListings
                  .slice()
                  .sort((a, b) => a.price - b.price)
                  .map((listing) => (
                    <ListingCard
                      key={listing.id}
                      $active={listing.id === effectiveSelectedId}
                      onClick={() => handleSelectListing(listing.id)}
                    >
                      <ListingThumbnail $isPlaceholder={!listing.images?.length}>
                        <img
                          src={listing.images?.[0] || book.coverImage}
                          alt={`Copia de ${listing.sellerName || 'vendedor'}`}
                        />
                        {!listing.images?.length && (
                          <ThumbnailLabel>Foto ilustrativa</ThumbnailLabel>
                        )}
                      </ListingThumbnail>
                      <ListingSellerInfo>
                        <ListingSellerName to={`/vendedor/${listing.sellerId}`}>
                          {listing.sellerName || 'Vendedor'}
                        </ListingSellerName>
                        <ListingMeta>
                          {listing.sellerRating != null && (
                            <StarRating value={listing.sellerRating} count={listing.sellerReviewCount} />
                          )}
                          <ConditionBadge condition={listing.condition} />
                          <ListingCopies>
                            {listing.copiesAvailable} {listing.copiesAvailable === 1 ? 'copia' : 'copias'}
                          </ListingCopies>
                        </ListingMeta>
                      </ListingSellerInfo>
                      <ListingPriceArea>
                        <PriceTag amount={listing.price} originalAmount={listing.originalPrice} />
                      </ListingPriceArea>
                    </ListingCard>
                  ))}
              </ListingsSection>
            )}

            {selectedListing && (
              <PriceSection>
                <PriceTag amount={selectedListing.price} originalAmount={selectedListing.originalPrice} />
              </PriceSection>
            )}

            {selectedListing && (
              <ActionRow>
                <InputNumber
                  min={1}
                  max={selectedListing.copiesAvailable}
                  value={quantity}
                  onChange={(val) => setQuantity(val || 1)}
                  size="large"
                />
                <Button
                  type="primary"
                  size="large"
                  icon={<ShoppingCartOutlined />}
                  onClick={handleAddToCart}
                >
                  Agregar al carrito
                </Button>
              </ActionRow>
            )}
          </RightColumn>
        </TwoColumnLayout>

        <SynopsisSection>
          <SynopsisTitle>Sinopsis</SynopsisTitle>
          <SynopsisText>{book.synopsis}</SynopsisText>
        </SynopsisSection>

        <Divider />

        <RecentSection>
          <RecentTitle>Visto Recientemente</RecentTitle>
          <RecentScroll>
            {!recentLoading && (recentBooks || []).map((recentBook) => (
              <BookCardSmall
                key={recentBook.id}
                id={recentBook.id}
                title={recentBook.title}
                coverImage={recentBook.coverImage}
                price={recentBook.price}
              />
            ))}
          </RecentScroll>
        </RecentSection>
      </PageWrapper>
    </MainLayout>
  );
}
